import React, { useState, useCallback } from 'react';
import { FileText, FolderOpen, BookOpen, ChevronRight, ChevronDown } from 'lucide-react';

/**
 * Iterative (non-recursive) structure tree for PM entries.
 * Avoids babel plugin stack overflow from recursive JSX.
 */
const StructureTree = ({ nodes }) => {
  // Track expanded state by path string
  const [expanded, setExpanded] = useState(() => {
    // Auto-expand first 2 levels
    const init = {};
    const walk = (items, prefix) => {
      items.forEach((n, i) => {
        const key = `${prefix}-${i}`;
        init[key] = true;
        if (n.children) walk(n.children, key);
      });
    };
    walk(nodes, 'r');
    return init;
  });

  const toggle = useCallback((key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Flatten tree into visible rows
  const rows = [];
  const flatten = (items, depth, prefix) => {
    items.forEach((node, i) => {
      const key = `${prefix}-${i}`;
      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = !!expanded[key];
      rows.push({ node, depth, key, hasChildren, isExpanded });
      if (hasChildren && isExpanded) {
        flatten(node.children, depth + 1, key);
      }
    });
  };
  flatten(nodes, 0, 'r');

  return (
    <div>
      {rows.map(({ node, depth, key, hasChildren, isExpanded }) => {
        const isDm = !!node.dmRef;
        const isPm = !!node.pmRef;
        return (
          <div
            key={key}
            className={`flex items-center gap-2 py-1.5 px-2 rounded text-sm hover:bg-slate-50 transition-colors cursor-pointer ${isDm ? '' : 'font-medium'}`}
            style={{ paddingLeft: 8 + depth * 16 }}
            onClick={() => hasChildren && toggle(key)}
            data-testid={`struct-${key}`}
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            ) : (
              <span className="w-3.5 shrink-0" />
            )}
            {isDm && <FileText className="w-3.5 h-3.5 text-teal-500 shrink-0" />}
            {isPm && <BookOpen className="w-3.5 h-3.5 text-violet-500 shrink-0" />}
            {!isDm && !isPm && hasChildren && <FolderOpen className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
            <span className="truncate">
              {isDm && <code className="text-xs font-mono text-teal-700 mr-2">{node.dmRef}</code>}
              {isPm && <code className="text-xs font-mono text-violet-700 mr-2">{node.pmRef}</code>}
              <span className={isDm ? 'text-slate-600' : 'text-slate-800'}>{node.title || '(untitled section)'}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default StructureTree;
